import i18n from '@/i18n/config';

// Fallback country mapping for when Intl.DisplayNames is not available (Top 50 countries)
const countryCodeToName: Record<string, string> = {
  // Major English-speaking countries
  'US': 'United States',
  'GB': 'United Kingdom',
  'CA': 'Canada',
  'AU': 'Australia',
  'NZ': 'New Zealand',
  'IE': 'Ireland',
  
  // Major European countries
  'DE': 'Germany',
  'FR': 'France',
  'ES': 'Spain',
  'IT': 'Italy',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'CH': 'Switzerland',
  'AT': 'Austria',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'PL': 'Poland',
  'PT': 'Portugal',
  'GR': 'Greece',
  'CZ': 'Czech Republic',
  'HU': 'Hungary',
  'RO': 'Romania',
  'HR': 'Croatia',
  'SK': 'Slovakia',
  
  // Major Asian countries
  'CN': 'China',
  'JP': 'Japan',
  'IN': 'India',
  'KR': 'South Korea',
  'TH': 'Thailand',
  'VN': 'Vietnam',
  'ID': 'Indonesia',
  'MY': 'Malaysia',
  'SG': 'Singapore',
  'PH': 'Philippines',
  'PK': 'Pakistan',
  'BD': 'Bangladesh',
  'TW': 'Taiwan',
  'HK': 'Hong Kong',
  
  // Major American countries
  'BR': 'Brazil',
  'MX': 'Mexico',
  'AR': 'Argentina',
  'CL': 'Chile',
  'CO': 'Colombia',
  'PE': 'Peru',
  'VE': 'Venezuela',
  'EC': 'Ecuador',
  
  // Major African countries
  'ZA': 'South Africa',
  'NG': 'Nigeria',
  'EG': 'Egypt',
  'KE': 'Kenya',
  'MA': 'Morocco',
  
  // Major Middle Eastern countries
  'TR': 'Turkey',
  'SA': 'Saudi Arabia',
  'AE': 'United Arab Emirates',
  'IL': 'Israel',
  'IR': 'Iran',
  
  // Major Eastern European/Asian countries
  'RU': 'Russia',
  'UA': 'Ukraine'
};

/**
 * Get country name from 2-letter country code
 * Uses Intl.DisplayNames API for localized country names
 */
export function getCountryName(countryCode: string): string | null {
  if (!countryCode || countryCode.length !== 2) {
    return null;
  }

  const upperCode = countryCode.toUpperCase();

  // Check if Intl.DisplayNames is available
  if (typeof Intl === 'undefined' || !Intl.DisplayNames) {
    //console.warn('Intl.DisplayNames not supported, using fallback mapping');
    return countryCodeToName[upperCode] || null;
  }

  try {
    // Get the current language directly from i18n instance
    const currentLanguage = i18n.language;
    const languageCode = currentLanguage === 'default' ? 'en' : currentLanguage;
    
    const displayNames = new Intl.DisplayNames([languageCode], {
      type: 'region',
      style: 'long'
    });

    const result = displayNames.of(upperCode);
    return result || countryCodeToName[upperCode] || null;
  } catch (error) {
    // Fallback to English if the current language is not supported
    try {
      const displayNames = new Intl.DisplayNames(['en'], {
        type: 'region',
        style: 'long'
      });
      const result = displayNames.of(upperCode);
      return result || countryCodeToName[upperCode] || null;
    } catch (fallbackError) {
      console.warn('Failed to get country name for code:', countryCode, 'Error:', fallbackError, 'Using fallback mapping');
      return countryCodeToName[upperCode] || null;
    }
  }
}

/**
 * @deprecated Use getCountryName directly instead
 * Custom hook wrapper for backward compatibility
 */
export function useCountryName() {
  return { getCountryName };
} 